import { Formik } from "formik";
import styled from "styled-components/macro";
import { useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Grid, Typography } from "@mui/material";

import pages from "../../../../constants/pages";
import {
  AutocompleteMultiSelect,
  Label,
  Autocomplete,
  TextField,
} from "../../../../components";
import { colors } from "../../../css components/Style";
import { useAxios, useComponent } from "../../../../hooks";
import { PrimaryButton } from "../../../css components/Button";

const Heading = styled(Typography)`
  font-size: 15px;
  font-weight: 500;
  line-height: 27px;
  align-self: center;
`;

export default function AudienceForm({
  audienceData,
  component,
  formikProps,
  editState,
}) {
  const axios = useAxios();
  const navigate = useNavigate();
  const { loader } = useComponent();
  const [fields, setFields] = useState([]);

  useEffect(() => {
    axios({
      url: "/adtech/getAllDynamicFields",
      method: "GET",
      disableRedirect: true,
    })
      .then((response) => {
        if (response.status) {
          setFields(response.data);
        }
      })
      .catch((err) => console.error(err));
  }, [axios, loader]);

  return (
    <Fragment>
      <Formik enableReinitialize initialValues={audienceData}>
        {(formikProps) => (
          <Fragment>
            <Grid item xs={6}>
              <Label title={"Audience Name"} />
              <TextField
                formikProps={formikProps}
                name="audience_group.value"
                placeholder="Audience Name"
                disabled
              />
            </Grid>

            {fields.map(
              (field, index) =>
                audienceData.hasOwnProperty([field.name]) && (
                  <Fragment key={index}>
                    {field.control_id === 3 && (
                      <Grid item xs={6}>
                        <Mandatory field={field} />

                        <TextField
                          value={audienceData[field.name]?.value}
                          name={`${field.name}.value`}
                          placeholder={field.label}
                          disabled
                        />
                      </Grid>
                    )}

                    {field.control_id === 1 && (
                      <Grid item xs={6}>
                        <Grid container spacing={3}>
                          <Mandatory
                            style={{
                              width: "100%",
                              paddingLeft: "16px",
                              paddingBottom: "3px",
                            }}
                            field={field}
                          />

                          <Grid item xs={6} style={{ paddingTop: "0px" }}>
                            <TextField
                              size="small"
                              value={
                                audienceData[field.name]?.value[
                                  field.options[0]["value"]
                                ]
                              }
                              name={`${field.name}.value.${field.options[0]["value"]}`}
                              placeholder={field.options[0].label}
                              disabled
                            />
                          </Grid>

                          <Grid item xs={6} style={{ paddingTop: "0px" }}>
                            <TextField
                              size="small"
                              value={
                                audienceData[field.name]?.value[
                                  field.options[1]["value"]
                                ]
                              }
                              name={`${field.name}.value.${field.options[1]["value"]}`}
                              placeholder={field.options[1].label}
                              disabled
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {field.control_id === 2 && field.multi_select && (
                      <Grid item xs={6}>
                        <Mandatory field={field} />

                        <AutocompleteMultiSelect
                          name={`${field.name}.value`}
                          value={audienceData[field.name]?.value}
                          disabled
                          defaults={{
                            primaryKey: "value",
                            displayLabel: "label",
                          }}
                          options={field.options}
                          formikProps={formikProps}
                          placeholder={field.placeholder}
                        />
                      </Grid>
                    )}

                    {field.control_id === 2 && !field.multi_select && (
                      <Grid item xs={6}>
                        <Mandatory field={field} />

                        <Autocomplete
                          name={`${field.name}.value`}
                          placeholder={field.placeholder}
                          defaults={{
                            primaryKey: "value",
                            displayLabel: "label",
                          }}
                          sx={{
                            "& .MuiSvgIcon-root": {
                              display: "none",
                            },
                          }}
                          formikProps={formikProps}
                          options={field.options}
                          value={audienceData[field.name]?.value}
                          disabled
                        />
                      </Grid>
                    )}

                    {field.control_id === 4 && (
                      <Grid item xs={6}>
                        <Mandatory field={field} />

                        <DesktopDatePicker
                          name={`${field.name}.value`}
                          formikProps={formikProps}
                          disabled
                          value={formikProps.values[field.name]?.value}
                          onChange={(newValue) => {
                            let date = new Date(newValue);

                            let d =
                              date.getFullYear() +
                              "-" +
                              "0" +
                              (date.getMonth() + 1) +
                              "-" +
                              date.getDate();

                            formikProps.setValues((prevVal) => ({
                              ...prevVal,
                              [field.name]: {
                                ...prevVal[field.name],
                                value: d,
                              },
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              disabled
                              name={`${field.name}.value`}
                              formikProps={formikProps}
                              {...params}
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Fragment>
                )
            )}

            {audienceData?.additionalFields?.map((field, index) => (
              <Fragment>
                <Grid style={{ color: colors.purple }} item xs={10}>
                  <Label title={"Additional Field " + (index + 1)} />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="Key"
                    disabled
                    value={field.key}
                    name={"additionalFields[" + index + "].key"}
                    placeholder="key"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    size="small"
                    disabled
                    label="Value"
                    value={field.value}
                    name={"additionalFields[" + index + "].value"}
                    placeholder="Value"
                  />
                </Grid>
              </Fragment>
            ))}
          </Fragment>
        )}
      </Formik>

      {component === "" &&
        !!formikProps.values.campaignID &&
        editState &&
        audienceData.isEditable && (
          <Grid item xs={12}>
            <PrimaryButton
              onClick={() =>
                navigate(
                  pages.audienceDetails.route + audienceData.ID + "?mode=edit"
                )
              }
              style={{
                color: colors.purple,
                cursor: "pointer",
                padding: "0px",
              }}
            >
              Edit Detail
            </PrimaryButton>
          </Grid>
        )}
    </Fragment>
  );

  function Mandatory({ field, style }) {
    return (
      <div
        style={{ display: "flex", justifyContent: "space-between", ...style }}
      >
        <Label title={field.label} />
      </div>
    );
  }
}
